import * as React from "react";
import { useState } from "react";
import { CALL_STATUS_LABELS, CallStatus, LEAD_ACTIONS, LEAD_STATUS, LEAD_STATUS_COLORS } from "@/constants";
import { formatDate } from "@/utils";
import { Box, Button, Card, Chip, Fade, Grid, Link, MenuItem, MenuList, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ListIcon } from "@phosphor-icons/react/dist/ssr/List";
import { UserIcon } from "@phosphor-icons/react/dist/ssr/User";
import { useParams } from "react-router-dom";

import { useCampaign, useCampaignLead } from "@/hooks/api/use-campaigns";
import { contactsKeys } from "@/hooks/api/use-contacts";
import { useDialog } from "@/hooks/use-dialog";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { CallLogs } from "@/components/call-logs";
import { useAnimation } from "@/components/core/animation-provider";
import { Dropdown } from "@/components/core/dropdown/dropdown";
import { DropdownContext } from "@/components/core/dropdown/dropdown-context";
import { DropdownPopover } from "@/components/core/dropdown/dropdown-popover";
import { DropdownTrigger } from "@/components/core/dropdown/dropdown-trigger";
import { InfoCard } from "@/components/core/info-card/info-card";
import { RouterLink } from "@/components/core/link";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";
import { FilterType } from "@/components/customTable/types";
import { StatisticCard } from "@/components/statisticCard/statistic-card";

import { UpdateLeadModal } from "../update-lead-modal";

const animationDelay = 300;

function MenuItemWithClose({
	children,
	onClick,
}: {
	children: React.ReactNode;
	onClick: () => void;
}): React.JSX.Element {
	const { onClose } = React.useContext(DropdownContext);

	const handleClick = () => {
		onClick();
		onClose();
	};

	return (
		<li>
			<MenuItem
				onClick={handleClick}
				sx={{
					fontSize: "14px",
					gap: 1,
					mt: 0.5,
				}}
			>
				{children}
			</MenuItem>
		</li>
	);
}

export function LeadView(): React.JSX.Element {
	const { isAnimated } = useAnimation();

	const [isInfoCardVisible, setIsInfoCardVisible] = useState(isAnimated);

	const { campaignId, leadId } = useParams();

	const { data: leadData, isLoading: isLeadLoading } = useCampaignLead(campaignId || "", leadId || "");
	const { data: campaignData, isLoading: isCampaignLoading } = useCampaign(campaignId || "");

	const {
		open: isUpdateLeadsModalOpen,
		data: selectedLeadsToUpdate,
		handleClose: handleUpdateLeadsModalClose,
		handleOpen: handleUpdateLeadsModalOpen,
	} = useDialog<any | null>();

	const isLoading = isLeadLoading;

	React.useEffect(() => {
		setTimeout(
			() => {
				setIsInfoCardVisible(true);
			},
			(animationDelay * (statisticCards.length + 1)) / 2
		);
	}, []);

	const statisticCards = React.useMemo(() => {
		const status = LEAD_STATUS.find((status) => status.value === leadData?.status);

		return [
			{
				title: "Contact Status",
				value: <Box whiteSpace="pre"> </Box>,
				icon: <UserIcon color="var(--mui-palette-primary-main)" width={20} height={20} />,
				additonalInfo: (
					<Chip
						label={status?.label}
						size="small"
						color={LEAD_STATUS_COLORS[status?.value || "pending"]}
						variant="outlined"
					/>
				),
			},
			{
				title: "Disposition",
				value: <Box whiteSpace="pre"> </Box>,
				icon: <ListIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
				additonalInfo: leadData?.disposition ? (
					<Chip label={leadData?.disposition} size="small" color={"default"} variant="filled" />
				) : (
					"N/A"
				),
			},
		];
	}, [leadData]);

	const campaignInfo = React.useMemo(() => {
		return {
			title: "Contact Information",
			subtitle: "Contact details",
			fields: [
				{ label: "Phone", value: leadData?.contact?.phone || "N/A", type: "text" as const },
				{
					label: "Tags",
					value: leadData?.contact?.tags.map((tag: any) => tag.label).join(", ") || "N/A",
					type: "tags" as const,
				},
				{ label: "Created At", value: formatDate(leadData?.contact?.createdAt) || "N/A", type: "date" as const },
				{ label: "Updated At", value: formatDate(leadData?.contact?.updatedAt) || "N/A", type: "date" as const },
				...(leadData?.contact?.attributes
					? Object.entries(leadData?.contact?.attributes).map(([key, value]) => ({
							label: key?.replace(/([a-z])([A-Z])/g, "$1 $2"),
							value: typeof value === "boolean" ? (value ? "Yes" : "No") : value?.toString(),
							type: "text" as const,
						}))
					: []),
			],
		};
	}, [leadData]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/campaigns"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Campaigns
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Link
								color="text.primary"
								component={RouterLink}
								href={`/campaigns/${campaignId}/view`}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								{isCampaignLoading ? <CustomSkeleton width={100} height={20} /> : campaignData?.title}
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Link
								color="text.primary"
								component={RouterLink}
								href={`/campaigns/${campaignId}/view?tab=contacts`}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								Contacts
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								{isLoading ? <CustomSkeleton width={100} height={20} /> : leadData?.contact?.phone}
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Box>
									<Typography variant="h4">Contact Details</Typography>
								</Box>
							</Box>
							<Dropdown>
								<DropdownTrigger>
									<Button startIcon={<ArrowsClockwiseIcon />} variant="outlined" onClick={() => {}}>
										Update Contact
									</Button>
								</DropdownTrigger>
								<DropdownPopover>
									<MenuList sx={{ px: 0.5, py: 0.5, minWidth: "150px" }} tabIndex={-1}>
										{LEAD_ACTIONS.map((action) => (
											<MenuItemWithClose
												key={action.label}
												onClick={() => {
													handleUpdateLeadsModalOpen({
														id: leadId,
														rows: [leadId],
														isAllDataSelected: false,
														action: action.value,
														label: action.label,
													});
												}}
											>
												{action.label}
											</MenuItemWithClose>
										))}
									</MenuList>
								</DropdownPopover>
							</Dropdown>
						</Stack>
					</Stack>

					<Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid>

					<Grid container spacing={3}>
						<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
							<Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
								<InfoCard
									title={campaignInfo.title}
									subtitle={campaignInfo.subtitle}
									fields={campaignInfo.fields}
									isLoading={isLoading}
								/>
							</Grid>
						</Fade>
					</Grid>

					<Card sx={{ position: "relative" }}>
						<CallLogs
							queryKey={contactsKeys.calls(leadId)}
							endpoint={`/calls?contactId=${leadId}&campaignId=${campaignId}`}
							filtersOptions={[
								{
									name: "status",
									label: "Status",
									value: [],
									type: FilterType.SELECT,
									options: Object.values(CALL_STATUS_LABELS).map((label) => ({
										label,
										value: CallStatus[label.replace(" ", "")],
									})),
								},
							]}
							hideCampaign
              campaign={campaignData}
							tableHeaderTitle="Contact Call Logs"
						/>
					</Card>
				</Stack>
			</Box>

			<UpdateLeadModal
				open={isUpdateLeadsModalOpen}
				onClose={handleUpdateLeadsModalClose}
				selectedLeads={selectedLeadsToUpdate || null}
			/>
		</React.Fragment>
	);
}
